/**
 * Modelli TypeScript per l'autenticazione
 * Le proprietà sono in camelCase per corrispondere alla serializzazione JSON del backend
 */

import { z } from 'zod';
import { Gender, GenderSchema } from './worker';

/**
 * Modello per il login basato su LoginModel C#
 */
export interface LoginModel {
	username: string;
	password: string;
}

/**
 * Modello per il refresh token basato su RefreshTokenModel C#
 */
export interface RefreshTokenModel {
	token: string;
	expiresAt: string;
	createdAt: string;
}

/**
 * Risposta di autenticazione basata su AuthenticateResponse C#
 */
export interface AuthenticateResponse {
	token: string;
	refreshToken: RefreshTokenModel;
}

/**
 * Modello per il cambio password basato su ChangePasswordModel C#
 */
export interface ChangePasswordModel {
	oldPassword: string;
	newPassword: string;
}

/**
 * Modello per il reset password basato su ResetPasswordModel C#
 */
export interface ResetPasswordModel {
	username: string;
	token: string;
	newPassword: string;
}

/**
 * DTO per le informazioni utente basato su ApplicationUserDto C# (serializzato in camelCase)
 */
export interface ApplicationUserDto {
	id: string;
	userName: string;
	displayName: string;
	active: boolean;
	email: string;
	roles: string[];
	workerId?: number;
	code: string;
	name: string;
	socialSecurityNumber: string;
	gender: Gender;
	avatarImage: string;
	/** true se l'utente può usare i Rimborsi per la restrizione BE `RefundsAccess`; assente con un BE precedente */
	refundsEnabled?: boolean;
}

// ===== SCHEMA ZOD PER VALIDAZIONE RUNTIME =====

/**
 * Schema Zod per la validazione del login
 */
export const LoginModelSchema = z.object({
	username: z.string().min(1, 'Username is required'),
	password: z.string().min(1, 'Password is required'),
});

/**
 * Schema Zod per la validazione del refresh token
 */
export const RefreshTokenModelSchema = z.object({
	token: z.string(),
	expiresAt: z.string(),
	createdAt: z.string(),
});

/**
 * Schema Zod per la validazione della risposta di autenticazione
 */
export const AuthenticateResponseSchema = z.object({
	token: z.string(),
	refreshToken: RefreshTokenModelSchema,
});

/**
 * Schema Zod per la validazione del cambio password
 */
export const ChangePasswordModelSchema = z.object({
	oldPassword: z.string().min(1, 'Old password is required'),
	newPassword: z.string().min(6, 'New password must be at least 6 characters'),
});

/**
 * Schema Zod per la validazione del reset password
 */
export const ResetPasswordModelSchema = z.object({
	username: z.string().min(1, 'Username is required'),
	token: z.string().min(1, 'Token is required'),
	newPassword: z.string().min(6, 'New password must be at least 6 characters'),
});

/**
 * Schema Zod per la validazione delle informazioni utente
 */
export const ApplicationUserDtoSchema = z.object({
	id: z.string(),
	userName: z.string(),
	displayName: z.string(),
	active: z.boolean(),
	email: z.string().email(),
	roles: z.array(z.string()),
	workerId: z.number().optional(),
	code: z.string(),
	name: z.string(),
	socialSecurityNumber: z.string(),
	gender: GenderSchema,
	avatarImage: z.string(),
	refundsEnabled: z.boolean().optional(),
});
